import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, DollarSign, Wrench, FileText, Shield, Zap, Users, CheckCircle, Menu, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const FEATURES = [
  { icon: <DollarSign size={20} />, title: 'Automated Payments', body: 'Set up rent payments with BPAY, Visa or Mastercard. Never miss a due date.' },
  { icon: <Wrench size={20} />, title: 'Maintenance Tracking', body: 'Submit requests with photos and receive real-time updates from your manager.' },
  { icon: <FileText size={20} />, title: 'Digital Documents', body: 'Lease agreements, receipts and inspection reports — all in one secure place.' },
  { icon: <Shield size={20} />, title: 'Secure & Compliant', body: 'Bank-level encryption, fully compliant with Australian tenancy legislation.' },
  { icon: <Zap size={20} />, title: 'Instant Notifications', body: 'Push alerts for payments, maintenance updates and important lease events.' },
  { icon: <Users size={20} />, title: 'Connected Parties', body: 'Tenants, owners, agents and contractors all communicate in one thread.' },
];

const BENEFITS = [
  'No hidden fees or surprise charges',
  'Cancel anytime — no lock-in contracts',
  'Works with all major property managers',
  'Mobile app for iOS and Android',
  '24/7 customer support',
  'Automatic payment receipts',
];

export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuthenticated, role } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isAuthenticated && role) {
      const map: Record<string, string> = {
        ADMIN: '/admin/dashboard', AGENT: '/agent/dashboard',
        CONTRACTOR: '/contractor/dashboard', OWNER: '/owner/dashboard', RENTER: '/renter/dashboard',
      };
      navigate(map[role] || '/renter/dashboard', { replace: true });
    }
  }, [isAuthenticated, role, navigate]);

  return (
    <div className="min-h-screen" style={{ fontFamily: 'var(--font-sans)', background: 'var(--ink-50)' }}>

      {/* ── Navbar ───────────────────────────────────────────── */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'shadow-sm' : ''}`}
        style={{ background: scrolled ? 'rgba(12,68,50,0.97)' : 'var(--forest-900)' }}
      >
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-8 font-bold text-[13px] flex items-center justify-center flex-none"
              style={{ background: 'var(--teal-400)', color: 'var(--forest-900)' }}>
              R
            </div>
            <span className="text-white font-semibold text-[15px]">Rentry</span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            {['#features', '#how-it-works', '#pricing'].map(href => (
              <a key={href} href={href} className="text-white/75 hover:text-white text-[13.5px] transition-colors">
                {href.replace('#', '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </a>
            ))}
            <Link to="/login" className="text-white/75 hover:text-white text-[13.5px] transition-colors">Sign in</Link>
            <Link to="/register"
              className="px-4 py-1.5 rounded-8 text-[13px] font-semibold transition-colors"
              style={{ background: 'var(--teal-400)', color: 'var(--forest-900)' }}>
              Get Started
            </Link>
          </div>

          <button className="md:hidden p-2 text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden px-6 pb-4 space-y-2 border-t border-white/10" style={{ background: 'var(--forest-900)' }}>
            {['#features', '#how-it-works', '#pricing'].map(href => (
              <a key={href} href={href} onClick={() => setMobileMenuOpen(false)}
                className="block py-2 text-white/80 hover:text-white text-[14px] transition-colors">
                {href.replace('#', '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </a>
            ))}
            <Link to="/login" className="block py-2 text-white/80 text-[14px]">Sign in</Link>
            <Link to="/register"
              className="block mt-2 py-2.5 text-center rounded-10 font-semibold text-[14px]"
              style={{ background: 'var(--teal-400)', color: 'var(--forest-900)' }}>
              Get Started
            </Link>
          </div>
        )}
      </nav>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section
        className="relative pt-36 pb-24 overflow-hidden"
        style={{ background: 'linear-gradient(170deg, var(--forest-900) 0%, var(--forest-700) 100%)' }}
      >
        {/* Decorative blob */}
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full opacity-10"
          style={{ background: 'var(--teal-400)', filter: 'blur(72px)' }} />

        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11.5px] font-medium mb-6"
              style={{ background: 'rgba(255,255,255,0.12)', color: 'var(--teal-400)' }}>
              Property management for everyone
            </div>
            <h1 className="text-[42px] sm:text-[56px] font-semibold text-white leading-tight mb-5">
              Rental management<br />
              <span style={{ color: 'var(--teal-400)' }}>made simple.</span>
            </h1>
            <p className="text-white/70 text-[16px] leading-relaxed mb-8 max-w-xl">
              Pay rent, track maintenance, and manage your entire tenancy — all in one platform.
              Built for tenants, landlords, agents and contractors.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/register"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-10 font-semibold text-[14.5px] transition-colors group"
                style={{ background: 'var(--teal-400)', color: 'var(--forest-900)' }}>
                Get Started Free
                <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link to="/login"
                className="inline-flex items-center justify-center px-6 py-3 rounded-10 font-semibold text-[14.5px] border border-white/25 text-white hover:bg-white/10 transition-colors">
                Sign in
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ─────────────────────────────────────────── */}
      <section id="features" className="py-20 bg-background">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-[30px] font-semibold text-ink-900 mb-3">Everything you need</h2>
            <p className="text-ink-500 text-[15px] max-w-xl mx-auto">
              Powerful features designed to make renting easier for everyone involved.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map(f => (
              <div key={f.title} className="bg-card rounded-12 border border-ink-100 p-6 hover:shadow-sm transition-shadow">
                <div className="w-9 h-9 rounded-8 flex items-center justify-center mb-4 text-white"
                  style={{ background: 'var(--forest-900)' }}>
                  {f.icon}
                </div>
                <h3 className="text-[14.5px] font-semibold text-ink-900 mb-1.5">{f.title}</h3>
                <p className="text-[13px] text-ink-500 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ─────────────────────────────────────── */}
      <section id="how-it-works" className="py-20" style={{ background: 'var(--ink-100)' }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-[30px] font-semibold text-ink-900 mb-3">How it works</h2>
            <p className="text-ink-500 text-[15px]">Get started in three simple steps.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              { n: '1', title: 'Create Account', body: 'Sign up with your email in under 2 minutes.' },
              { n: '2', title: 'Connect Lease', body: 'Link your rental property and verify your tenancy.' },
              { n: '3', title: 'Start Managing', body: 'Pay rent, track maintenance, and communicate easily.' },
            ].map((step, i) => (
              <div key={i} className="bg-card rounded-12 border border-ink-100 p-6 text-center">
                <div className="w-11 h-11 rounded-full font-bold text-lg flex items-center justify-center mx-auto mb-4 text-white"
                  style={{ background: 'var(--forest-900)' }}>
                  {step.n}
                </div>
                <h3 className="text-[14.5px] font-semibold text-ink-900 mb-1.5">{step.title}</h3>
                <p className="text-[13px] text-ink-500 leading-relaxed">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Benefits ─────────────────────────────────────────── */}
      <section id="pricing" className="py-20 bg-background">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-4xl mx-auto">
            <div>
              <h2 className="text-[30px] font-semibold text-ink-900 mb-4">Why choose Rentry?</h2>
              <p className="text-ink-500 text-[15px] mb-7 leading-relaxed">
                We're committed to making renting transparent, efficient and stress-free for everyone involved.
              </p>
              <div className="space-y-3">
                {BENEFITS.map(b => (
                  <div key={b} className="flex items-start gap-3">
                    <CheckCircle size={18} className="flex-none mt-0.5" style={{ color: 'var(--forest-700)' }} />
                    <span className="text-[13.5px] text-ink-700">{b}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-card rounded-16 border border-ink-100 p-8 space-y-4">
              {[
                { label: 'Tenants', price: 'Free', note: 'Always free for renters' },
                { label: 'Property owners', price: 'Free', note: 'Free with agency plan' },
                { label: 'Agencies', price: '$9.99/lease/mo', note: 'Enterprise plans available' },
              ].map(plan => (
                <div key={plan.label} className="flex items-center justify-between py-3 border-b border-ink-100 last:border-0">
                  <div>
                    <p className="text-[14px] font-medium text-ink-900">{plan.label}</p>
                    <p className="text-[12px] text-ink-500">{plan.note}</p>
                  </div>
                  <span className="text-[14px] font-semibold text-ink-900 tabular-nums">{plan.price}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────── */}
      <section className="py-16" style={{ background: 'linear-gradient(135deg, var(--forest-900), var(--forest-700))' }}>
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-[28px] font-semibold text-white mb-3">Ready to get started?</h2>
          <p className="text-white/70 text-[15px] mb-7">
            Join thousands of renters managing their tenancy with Rentry.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-10 font-semibold text-[14px]"
              style={{ background: 'var(--teal-400)', color: 'var(--forest-900)' }}>
              Create Free Account
              <ArrowRight size={15} />
            </Link>
            <Link to="/agency/register"
              className="inline-flex items-center justify-center px-6 py-3 rounded-10 font-semibold text-[14px] border border-white/25 text-white hover:bg-white/10 transition-colors">
              Register Your Agency
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────── */}
      <footer className="py-10 bg-ink-900">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
            {[
              { heading: 'Product', links: [['Features', '#features'], ['Pricing', '#pricing'], ['Security', '#']] },
              { heading: 'Company', links: [['About', '#'], ['Blog', '#'], ['Careers', '#']] },
              { heading: 'Support', links: [['Help Center', '#'], ['Contact', '#'], ['FAQ', '#']] },
              { heading: 'Legal', links: [['Privacy', '#'], ['Terms', '#'], ['Compliance', '#']] },
            ].map(col => (
              <div key={col.heading}>
                <h4 className="text-[11px] font-semibold uppercase tracking-wider text-ink-500 mb-3">{col.heading}</h4>
                <ul className="space-y-2">
                  {col.links.map(([label, href]) => (
                    <li key={label}>
                      <a href={href} className="text-[13px] text-ink-400 hover:text-white transition-colors">{label}</a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="pt-6 border-t border-ink-800 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-8 font-bold text-[12px] flex items-center justify-center"
                style={{ background: 'var(--teal-400)', color: 'var(--forest-900)' }}>
                R
              </div>
              <span className="text-white font-semibold text-[14px]">Rentry</span>
            </div>
            <p className="text-[12px] text-ink-500">© {new Date().getFullYear()} Rentry. All rights reserved.</p>
          </div>
        </div>
      </footer>

    </div>
  );
}

